import React, { useState, useEffect } from 'react';
import { X, Plus, Tag, Users, Lock, Eye, EyeOff } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useAuth } from '../../contexts/AuthContext';
import { CommunityService } from '../../services/communityService';
import type { CreateForumData, ForumCategory } from '../../types/community';

interface CreateForumModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateForumModal: React.FC<CreateForumModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { language, isRTL, strings } = useLocalization();
  const { presetData, loading: presetLoading } = useSystemPreset();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [formData, setFormData] = useState<CreateForumData>({
    title: '',
    description: '',
    subject: '',
    categoryId: '',
    rules: '',
    tags: [],
    isPublic: true,
    allowThreadCreation: true,
    requireApproval: false,
    icon: '',
    color: '#1A2B4C'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTag, setNewTag] = useState('');

  // Debug logging
  console.log('🔍 CreateForumModal - Categories loaded:', categories.length);
  console.log('🔍 CreateForumModal - Preset data available:', !!presetData);
  console.log('🔍 CreateForumModal - Forum categories from preset:', presetData?.forumCategories?.length || 0);
  console.log('🔍 CreateForumModal - Preset loading:', presetLoading);

  // Predefined forum colors
  const forumColors = [
    { value: '#1A2B4C', name: 'Primary Blue' },
    { value: '#059669', name: 'Green' },
    { value: '#DC2626', name: 'Red' },
    { value: '#7C3AED', name: 'Purple' },
    { value: '#EA580C', name: 'Orange' },
    { value: '#0891B2', name: 'Cyan' },
    { value: '#BE185D', name: 'Pink' },
    { value: '#65A30D', name: 'Lime' }
  ];

  // Predefined forum icons
  const forumIcons = [
    { value: 'gavel', name: 'Gavel' },
    { value: 'scale', name: 'Scale' },
    { value: 'book', name: 'Book' },
    { value: 'users', name: 'Users' },
    { value: 'message-circle', name: 'Discussion' },
    { value: 'briefcase', name: 'Briefcase' },
    { value: 'shield', name: 'Shield' },
    { value: 'star', name: 'Star' }
  ];

  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (presetData?.forumCategories && presetData.forumCategories.length > 0) {
      loadCategories();
    }
  }, [presetData]);

  const loadCategories = async () => {
    try {
      console.log('🔍 CreateForumModal - Loading categories...');
      
      // First try to load from SystemPresetData
      if (presetData?.forumCategories && presetData.forumCategories.length > 0) {
        console.log('🔍 CreateForumModal - Loading from SystemPresetData');
        console.log('🔍 CreateForumModal - Raw forum categories:', presetData.forumCategories);
        
        // Check if categories have isActive property
        const activeCategories = presetData.forumCategories.filter(category => {
          console.log('🔍 CreateForumModal - Category:', category.id, 'isActive:', category.isActive);
          return category.isActive;
        });
        console.log('🔍 CreateForumModal - Active categories count:', activeCategories.length);
        
        // Temporarily show all categories regardless of isActive status
        const transformedCategories = presetData.forumCategories
          .map(category => {
            console.log('🔍 CreateForumModal - Transforming category:', category.id, category.name_en, 'isActive:', category.isActive);
            return {
              id: category.id,
              name: category.name_en,
              nameAr: category.name_ar,
              description: category.description,
              descriptionAr: category.descriptionAr,
              icon: category.icon,
              color: category.color,
              order: category.order,
              isActive: category.isActive
            };
          })
          .sort((a, b) => a.order - b.order);
        
        setCategories(transformedCategories);
        console.log('🔍 CreateForumModal - Categories loaded from SystemPresetData:', transformedCategories.length);
        console.log('🔍 CreateForumModal - Transformed categories:', transformedCategories);
      } else {
        // Fallback to CommunityService
        console.log('🔍 CreateForumModal - Loading from CommunityService (fallback)');
        const categoriesData = await CommunityService.getForumCategories();
        setCategories(categoriesData);
        console.log('🔍 CreateForumModal - Categories loaded from CommunityService:', categoriesData.length);
      }
    } catch (error) {
      console.error('❌ CreateForumModal - Error loading categories:', error);
      setCategories([]);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      subject: '',
      categoryId: '',
      rules: '',
      tags: [],
      isPublic: true,
      allowThreadCreation: true,
      requireApproval: false,
      icon: '',
      color: '#1A2B4C'
    });
    setErrors({});
    setNewTag('');
  };

  const handleInputChange = (field: keyof CreateForumData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = strings.common?.required || 'This field is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = strings.common?.required || 'This field is required';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = strings.common?.required || 'This field is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = strings.common?.required || 'This field is required';
    }

    if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (formData.subject.length > 1000) {
      newErrors.subject = 'Subject must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (!user?.id) {
        setErrors({ submit: 'User not authenticated. Please log in and try again.' });
        return;
      }
      
      const forumId = await CommunityService.createForum(formData, user.id);
      console.log('✅ CreateForumModal - Forum created successfully with ID:', forumId);
      console.log('📝 CreateForumModal - Forum data:', {
        title: formData.title,
        categoryId: formData.categoryId,
        isActive: true,
        isDeleted: false
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating forum:', error);
      setErrors({ submit: 'Failed to create forum. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            {strings.community?.createNewForum || 'Create New Forum'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {strings.common?.title || 'Title'} *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.title ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
              }`}
              placeholder="Enter forum title"
              maxLength={100}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {strings.common?.description || 'Description'} *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.description ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
              }`}
              placeholder="Describe what this forum is about"
              maxLength={500}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {language === 'ar' ? 'الموضوع' : 'Subject'} *
            </label>
            <textarea
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.subject ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
              }`}
              placeholder={language === 'ar' ? 'اكتب الموضوع الرئيسي للمناقشة هنا...' : 'Write the main subject/content paragraph here...'}
              maxLength={1000}
            />
            <div className="mt-1 flex justify-between text-xs text-slate-500">
              <span>{errors.subject && <span className="text-red-600">{errors.subject}</span>}</span>
              <span>{formData.subject.length}/1000</span>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {strings.community?.allCategories || 'Category'} *
            </label>
            <select
              value={formData.categoryId}
              onChange={(e) => handleInputChange('categoryId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.categoryId ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
              }`}
              disabled={presetLoading}
            >
              <option value="">
                {presetLoading ? 
                  (language === 'ar' ? 'جاري تحميل الفئات...' : 'Loading categories...') : 
                  (language === 'ar' ? 'اختر فئة' : 'Select a category')
                }
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {language === 'ar' ? category.nameAr : category.name}
                </option>
              ))}
            </select>
            {errors.categoryId && (
              <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
            )}
          </div>

          {/* Rules */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {strings.community?.rules || 'Forum Rules'} (Optional)
            </label>
            <textarea
              value={formData.rules}
              onChange={(e) => handleInputChange('rules', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Set rules and guidelines for this forum"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {strings.common?.tags || 'Tags'}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
              {strings.common?.settings || 'Settings'}
            </h3>
            
            {/* Public/Private */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {formData.isPublic ? (
                  <Eye className="w-5 h-5 text-slate-500" />
                ) : (
                  <EyeOff className="w-5 h-5 text-slate-500" />
                )}
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {strings.community?.isPublic || 'Public Forum'}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 dark:peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Allow Thread Creation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {strings.community?.allowThreadCreation || 'Allow Thread Creation'}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.allowThreadCreation}
                  onChange={(e) => handleInputChange('allowThreadCreation', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 dark:peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Require Approval */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {strings.community?.requireApproval || 'Require Approval for Threads'}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requireApproval}
                  onChange={(e) => handleInputChange('requireApproval', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 dark:peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>

          {/* Color and Icon */}
          <div className="grid grid-cols-2 gap-4">
            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {strings.community?.color || 'Color'}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {forumColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleInputChange('color', color.value)}
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color.value ? 'border-slate-800 dark:border-white' : 'border-slate-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {strings.community?.icon || 'Icon'}
              </label>
              <select
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select an icon</option>
                {forumIcons.map((icon) => (
                  <option key={icon.value} value={icon.value}>
                    {icon.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className={`flex gap-3 pt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {strings.common?.cancel || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (strings.common?.saving || 'Creating...') : (strings.community?.createForum || 'Create Forum')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateForumModal;
